/**
 * NGX (Nigerian Exchange) fee calculator.
 *
 * Computes the per-order cost breakdown displayed in the Order Ticket. All
 * monetary inputs are fixed-point kobo (string), all monetary outputs are
 * fixed-point kobo (string) — the UI converts to Naira at the very edge via
 * `koboToNaira()`.
 *
 * Source: NGX market rules + typical retail broker schedule (2025).
 *  - Brokerage:        1.35% of consideration (broker-specific; retail default)
 *  - SEC/NGX trade:    0.30% of consideration
 *  - CSCS settlement:  0.06% of consideration
 *  - Stamp Duty:       0.075% of consideration — BUY side only
 *  - Terminal Fee:     0.25% of consideration (Nigastock platform fee)
 *  - VAT on Brokerage: 7.5% of brokerage
 *  - VAT on SEC:       7.5% of SEC
 *  - VAT on CSCS:      5%   of CSCS
 *
 * Fees are stored as basis points (1bp = 0.01%) so we can do all math in bigint
 * and never touch JS floats. Rates are configurable so the broker partnership
 * team can tune Nigastock's Brokerage + Terminal Fee per broker without code
 * changes — the BAL would inject these via /v1/broker/fee-schedule in prod.
 */

export interface NgxFeeRates {
  brokerageBps: bigint;     // 135 = 1.35%
  secBps: bigint;           // 30  = 0.30%
  cscsBps: bigint;          // 6   = 0.06%
  stampDutyBps: bigint;     // 7.5 → represented as 75 (we'll divide by 100_000)
  terminalBps: bigint;      // 25  = 0.25%
  vatBrokeragePctBps: bigint; // 750 = 7.5%
  vatSecPctBps: bigint;       // 750 = 7.5%
  vatCscsPctBps: bigint;      // 500 = 5%
}

export const DEFAULT_NGX_RATES: NgxFeeRates = {
  brokerageBps: 135n,
  secBps: 30n,
  cscsBps: 6n,
  stampDutyBps: 75n,        // 0.075% — represented in tenths-of-bps internally below
  terminalBps: 25n,
  vatBrokeragePctBps: 750n, // 7.5%
  vatSecPctBps: 750n,
  vatCscsPctBps: 500n,
};

export type Side = 'BUY' | 'SELL';

export interface FeeBreakdown {
  side: Side;
  consideration: string;       // qty * price (kobo)
  brokerage: string;
  vatOnBrokerage: string;
  secFee: string;
  vatOnSec: string;
  cscsFee: string;
  vatOnCscs: string;
  stampDuty: string;           // 0n on SELL
  terminalFee: string;
  totalFees: string;
  /** BUY: consideration + totalFees · SELL: consideration - totalFees */
  totalCost: string;
  /** Display percentages so the UI doesn't have to recompute. */
  rates: {
    brokeragePct: string; secPct: string; cscsPct: string;
    stampDutyPct: string; terminalPct: string;
    vatBrokeragePct: string; vatSecPct: string; vatCscsPct: string;
  };
}

/**
 * Compute the fee breakdown for an order. `quantity` is shares (integer).
 * `priceKobo` is a fixed-point kobo string (e.g. "11147" for ₦111.47).
 */
export function computeNgxFees(
  side: Side, quantity: number, priceKobo: string, rates: NgxFeeRates = DEFAULT_NGX_RATES,
): FeeBreakdown {
  if (!Number.isInteger(quantity) || quantity <= 0) throw new Error('quantity must be positive integer');
  if (!/^\d+$/.test(priceKobo)) throw new Error('priceKobo must be fixed-point kobo string');

  const px = BigInt(priceKobo);
  const qty = BigInt(quantity);
  const consideration = px * qty; // kobo

  // pctOfBps10000 = bps/10000 = pct/100. We use BPS (basis points) where
  // 100bps = 1%. Stamp duty's 0.075% = 7.5 bps but rates above store it as 75
  // tenths-of-bps; divide by 1000 instead of 10000 for that one.
  const ofBps = (amount: bigint, bps: bigint) => (amount * bps) / 10000n;
  const ofTenthsOfBps = (amount: bigint, tbps: bigint) => (amount * tbps) / 100000n;

  const brokerage = ofBps(consideration, rates.brokerageBps);
  const secFee = ofBps(consideration, rates.secBps);
  const cscsFee = ofBps(consideration, rates.cscsBps);
  const stampDuty = side === 'BUY' ? ofTenthsOfBps(consideration, rates.stampDutyBps) : 0n;
  const terminalFee = ofBps(consideration, rates.terminalBps);

  const vatOnBrokerage = ofBps(brokerage, rates.vatBrokeragePctBps);
  const vatOnSec = ofBps(secFee, rates.vatSecPctBps);
  const vatOnCscs = ofBps(cscsFee, rates.vatCscsPctBps);

  const totalFees = brokerage + vatOnBrokerage + secFee + vatOnSec
                  + cscsFee + vatOnCscs + stampDuty + terminalFee;

  const totalCost = side === 'BUY' ? consideration + totalFees : consideration - totalFees;

  return {
    side,
    consideration: consideration.toString(),
    brokerage: brokerage.toString(),
    vatOnBrokerage: vatOnBrokerage.toString(),
    secFee: secFee.toString(),
    vatOnSec: vatOnSec.toString(),
    cscsFee: cscsFee.toString(),
    vatOnCscs: vatOnCscs.toString(),
    stampDuty: stampDuty.toString(),
    terminalFee: terminalFee.toString(),
    totalFees: totalFees.toString(),
    totalCost: totalCost.toString(),
    rates: {
      brokeragePct: bpsToPct(rates.brokerageBps),
      secPct: bpsToPct(rates.secBps),
      cscsPct: bpsToPct(rates.cscsBps),
      stampDutyPct: tenthBpsToPct(rates.stampDutyBps),
      terminalPct: bpsToPct(rates.terminalBps),
      vatBrokeragePct: bpsToPct(rates.vatBrokeragePctBps),
      vatSecPct: bpsToPct(rates.vatSecPctBps),
      vatCscsPct: bpsToPct(rates.vatCscsPctBps),
    },
  };
}

function bpsToPct(bps: bigint): string {
  // 135bps -> 1.35
  const whole = bps / 100n;
  const frac = bps % 100n;
  if (frac === 0n) return whole.toString();
  return `${whole}.${String(frac).padStart(2, '0').replace(/0+$/, '') || '0'}`;
}
function tenthBpsToPct(tbps: bigint): string {
  // 75 tenths-of-bps -> 0.075
  const whole = tbps / 1000n;
  const frac = tbps % 1000n;
  if (frac === 0n) return whole.toString();
  return `${whole}.${String(frac).padStart(3, '0').replace(/0+$/, '') || '0'}`;
}

/** Compact display: ₦11.37K, ₦11.15K, ₦150.48 */
export function compactNaira(kobo: string): string {
  if (!/^-?\d+$/.test(kobo)) return '—';
  const neg = kobo.startsWith('-');
  const abs = BigInt(neg ? kobo.slice(1) : kobo);
  const naira = Number(abs) / 100;
  let out: string;
  if (naira >= 1_000_000) out = `${(naira / 1_000_000).toFixed(2)}M`;
  else if (naira >= 1_000) out = `${(naira / 1_000).toFixed(2)}K`;
  else out = naira.toFixed(2);
  return `${neg ? '-' : ''}₦${out}`;
}
